import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { ForwardIcon } from "../../../automationStudioIcons";
import ConfirmationModal from "../../../../Modal/ConfirmationModal";
import styles from "../../../../../styles/automationStudio/automationStudio.module.scss";
import { LABELS } from "../../../content";
import {
    resolveDynamicStepIdentity,
    getDynamicBindingsForStep,
} from "../../../../AutomationStudio/utils/common";
import {
    parseDynamicIntentLabel,
    resolveDynamicIntentRaw,
    buildUniqueSourceKeyBindingRows,
    uniqueIntentTokensFromBindings,
} from "../../../../AutomationStudio/utils/stepMetaInfo";
import {
    buildUuidToDisplayStepNumberMap,
    bindingPlaceholderDisplay,
} from "./stepEditorDisplayHelpers";
import { useStepEditorDisplayContext } from "./useStepEditorDisplayContext";
import StepEditorHeader from "./StepEditorHeader";
import StepEditorDescriptionSection from "./StepEditorDescriptionSection";
import StepEditorTabPanels from "./StepEditorTabPanels";
import { selectSelectedStepFieldDirtyMap } from "../../../../../features/automationStudioNew/automationStudioNewSelectors";

export default function StepEditorView({
    step: stepFromProps,
    instructions = [],
    isExecuting = false,
    isGenerating = false,
    isReplayExecuting = false,
    mcpToolsList = [],
    mcpToolMap = {},
    replayExecutionResults = null,
    replayProgressMap = {},
    replayCurrentStepIndex = null,
    isLoadedFromTestcase = false,
    hasExecutedInSession = false,
    isReplayFlow = false,
    isSavedExecutionRecord = false,
    fileSuggestions = [],
    fileDescriptions = {},
    variablesList = [],
    variablesListDescriptions = {},
    onStepUpdate,
    onBackToInstructions,
    onBackToResults,
    onPrevStep,
    onNextStep,
    onDeleteStep,
    testCompleted = false,
    showPreviewTab = false,
    selectedFlatStepIndex = null,
    deleteStepWarning = null,
    dynamicResult = null,
    selectorsTabProps = {},
}) {
    const step = stepFromProps;
    const [activeTab, setActiveTab] = useState(0);
    const [localStep, setLocalStep] = useState(step);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [isHoveringDescription, setIsHoveringDescription] = useState(false);
    const descriptionFileSuggestions = fileSuggestions;
    const fieldDirtyMap = useSelector(selectSelectedStepFieldDirtyMap);

    const metaInfoModel = useMemo(() => {
        if (!localStep?.id) {
            return { intentType: '', intentText: '', bindingRows: [], intentTokens: [] };
        }
        const identity = resolveDynamicStepIdentity({
            step: localStep,
            stepIndex: localStep.stepIndex,
            fallbackToIndex: true,
        });
        const rawBindings = getDynamicBindingsForStep(dynamicResult?.dynamicBindings, identity);
        const intentFromStep = (() => {
            const ds = localStep?.dynamicIntent;
            if (ds === undefined || ds === null) return null;
            const t = String(ds).trim();
            return t || null;
        })();
        const intentRaw =
            intentFromStep
            ?? resolveDynamicIntentRaw(
                dynamicResult,
                localStep.id,
                localStep.stepIndex,
                identity.candidateKeys,
            );
        const { type: intentType, intent: intentText } = parseDynamicIntentLabel(intentRaw);
        const uuidToDisplayStep = buildUuidToDisplayStepNumberMap(instructions);
        const bindingRows = buildUniqueSourceKeyBindingRows(rawBindings).map((row) => ({
            ...row,
            valueDisplay: bindingPlaceholderDisplay(row.placeholder, uuidToDisplayStep),
        }));
        return {
            intentType,
            intentText,
            bindingRows,
            intentTokens: uniqueIntentTokensFromBindings(rawBindings),
        };
    }, [dynamicResult, instructions, localStep?.id, localStep?.stepId, localStep?.stepIndex, localStep?.dynamicIntent]);

    const hasStepMetaInfo = useMemo(() => {
        const m = metaInfoModel;
        return Boolean(
            (m.intentType && String(m.intentType).trim()) ||
            (m.intentText && String(m.intentText).trim()) ||
            (Array.isArray(m.bindingRows) && m.bindingRows.length > 0) ||
            (Array.isArray(m.intentTokens) && m.intentTokens.length > 0)
        );
    }, [metaInfoModel]);

    const showDynamicStepBadge = useMemo(() => {
        const list = dynamicResult?.dynamicStepIds;
        if (!Array.isArray(list) || list.length === 0) return false;
        const idSet = new Set(list.map((x) => String(x)));
        const candidates = [localStep?.id, localStep?.stepId, step?.id, step?.stepId]
            .filter((x) => x != null && String(x).trim() !== "")
            .map((x) => String(x));
        return candidates.some((id) => idSet.has(id));
    }, [dynamicResult?.dynamicStepIds, localStep?.id, localStep?.stepId, step?.id, step?.stepId]);

    useEffect(() => {
        if (!step) return;
        setLocalStep(step);
        setIsEditingDescription(false);
        setIsHoveringDescription(false);
    }, [step?.id]);

    useEffect(() => {
        if (!step) return;
        setLocalStep((prev) => ({
            ...prev,
            toolError: step.toolError,
            argsError: step.argsError,
            stepError: step.stepError,
        }));
    }, [step?.toolError, step?.argsError, step?.stepError, step?.id]);

    useEffect(() => {
        if (!step) return;
        const newIsVerificationStep = step.tool === 'evaluateTestStep' ||
            ((step.expectedOutcome !== undefined && step.actualOutcome !== undefined) || step.passed !== undefined);
        const newShowSelectorsTab = false;
        const metaExtra = hasStepMetaInfo ? 1 : 0;
        const newMaxTabIndex = newIsVerificationStep
            ? metaExtra
            : (!showPreviewTab ? (newShowSelectorsTab ? 2 : 1) + metaExtra : (newShowSelectorsTab ? 3 : 2) + metaExtra);
        setActiveTab((prevTab) => prevTab > newMaxTabIndex ? 0 : prevTab);
    }, [step?.id, showPreviewTab, hasStepMetaInfo, dynamicResult]);

    const display = useStepEditorDisplayContext({
        step, localStep, instructions, isExecuting, isGenerating, isReplayExecuting,
        isLoadedFromTestcase, hasExecutedInSession, isReplayFlow, testCompleted,
        replayExecutionResults,
        replayProgressMap, replayCurrentStepIndex, selectedFlatStepIndex, showPreviewTab, hasStepMetaInfo, activeTab,
    });

    if (!step) {
        return (
            <div className={styles.stepEditorPlaceholder}>
                <p className={`${styles.asTypoBody2} ${styles.asTextSecondary}`}>
                    {LABELS.NO_STEP_SELECTED}
                </p>
            </div>
        );
    }

    const {
        showResultsBackButton, isVerificationStep, isEditorLocked, recordingSkipReason,
        isRecordingSkipLocked, displayStep, showSelectorsTab, isEvaluationTool,
        safeActiveTab, verificationUiState, readOnlyEditor,
        hasPrevious, hasNext, replayEval, detailForUi, executedCodeStrategyType,
        shouldMaskRecordingOutcome, replayDetail, statusOptions,
    } = display;

    const handleLocalStepChange = (updates, { silent = false } = {}) => {
        setLocalStep((prev) => {
            const next = { ...prev, ...updates };
            if (Object.prototype.hasOwnProperty.call(updates, 'tool')) {
                delete next.toolError;
            }
            if (Object.prototype.hasOwnProperty.call(updates, 'args')) {
                delete next.argsError;
            }
            if (onStepUpdate) onStepUpdate(next);
            return next;
        });
        if (Object.prototype.hasOwnProperty.call(updates, 'tool') && updates.tool) {
            setActiveTab(1);
        }
    };

    const handleDelete = () => setShowDeleteConfirm(true);
    const handleConfirmDelete = () => {
        if (onDeleteStep) onDeleteStep(step.id);
        setShowDeleteConfirm(false);
    };

    return (
        <div className={styles.stepEditorContainer}>
            <StepEditorHeader
                showResultsBackButton={showResultsBackButton}
                onBackToResults={onBackToResults}
                onBackToInstructions={onBackToInstructions}
                onPrevStep={onPrevStep}
                onNextStep={onNextStep}
                isEditorLocked={isEditorLocked}
                hasPrevious={hasPrevious}
                hasNext={hasNext}
                onDelete={handleDelete}
                isExecuting={isExecuting}
            />

            {isRecordingSkipLocked && (
                <div className={styles.recordingSkipNotice} role="status">
                    <ForwardIcon className={styles.recordingSkipNoticeIcon} aria-hidden />
                    <div className={styles.recordingSkipNoticeBody}>
                        <span className={styles.recordingSkipNoticeTitle}>
                            {LABELS.RECORDING_SKIP_NOTICE}
                        </span>
                        <span className={`${styles.recordingSkipNoticeText} ${styles.asTypoCaption}`}>
                            {recordingSkipReason || LABELS.RECORDING_SKIP_REMOVE_ONLY}
                        </span>
                    </div>
                </div>
            )}

            <StepEditorDescriptionSection
                localStep={localStep}
                displayStep={displayStep}
                isEditorLocked={isEditorLocked}
                isEditingDescription={isEditingDescription}
                setIsEditingDescription={setIsEditingDescription}
                isHoveringDescription={isHoveringDescription}
                setIsHoveringDescription={setIsHoveringDescription}
                handleLocalStepChange={handleLocalStepChange}
                variablesList={variablesList}
                variablesListDescriptions={variablesListDescriptions}
                descriptionFileSuggestions={descriptionFileSuggestions}
                fileDescriptions={fileDescriptions}
                isVerificationStep={isVerificationStep}
                isEvaluationTool={isEvaluationTool}
                isReplayFlow={isReplayFlow}
                showDynamicStepBadge={showDynamicStepBadge}
                replayDetail={replayDetail}
                detailForUi={detailForUi}
                instructions={instructions}
                statusOptions={statusOptions}
                verificationUiState={verificationUiState}
                shouldMaskRecordingOutcome={shouldMaskRecordingOutcome}
                isDescriptionDirty={Boolean(fieldDirtyMap?.description)}
            />

            <StepEditorTabPanels
                isVerificationStep={isVerificationStep}
                safeActiveTab={safeActiveTab}
                setActiveTab={setActiveTab}
                hasStepMetaInfo={hasStepMetaInfo}
                showSelectorsTab={showSelectorsTab}
                showPreviewTab={showPreviewTab}
                localStep={localStep}
                displayStep={displayStep}
                handleLocalStepChange={handleLocalStepChange}
                isEditorLocked={isEditorLocked}
                readOnlyEditor={readOnlyEditor}
                mcpToolsList={mcpToolsList}
                mcpToolMap={mcpToolMap}
                executedCodeStrategyType={executedCodeStrategyType}
                fileSuggestions={fileSuggestions}
                fileDescriptions={fileDescriptions}
                variablesList={variablesList}
                variablesListDescriptions={variablesListDescriptions}
                instructions={instructions}
                replayExecutionResults={replayExecutionResults}
                replayProgressMap={replayProgressMap}
                isReplayFlow={isReplayFlow}
                showDynamicStepBadge={showDynamicStepBadge}
                replayEval={replayEval}
                detailForUi={detailForUi}
                statusOptions={statusOptions}
                metaInfoModel={metaInfoModel}
                selectorsTabProps={selectorsTabProps}
                fieldDirtyMap={fieldDirtyMap}
            />

            <ConfirmationModal
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                title={deleteStepWarning?.title || "Delete step?"}
                message={deleteStepWarning?.message || "Are you sure you want to delete this step? This action cannot be undone."}
            />
        </div>
    );
}
